import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@mui/material";
import { useMemo } from "react";

interface Props {
  open: boolean;
  setMoreInfoModalOpen: any;
}

interface PeriodConfig {
  startDate: string | null;
  endDate: string;
  editableCount: number;
  lockedCount: number;
}

const PeriodMoreInfoModal = ({
  open,
  setMoreInfoModalOpen,
}: Props) => {
  
  const handleClose = () => {
    setMoreInfoModalOpen(false);
  };

  // Helper function to get month range
  const getMonthRange = (startMonth: number, endMonth: number): string => {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    
    if (startMonth === endMonth) {
      return months[startMonth - 1];
    }
    return `${months[startMonth - 1]} - ${months[endMonth - 1]}`;
  };

  // Helper function to format month info
  const formatMonthInfo = (count: number, startMonth: number, endMonth: number): string => {
    if (count === 0) return "None";
    const range = getMonthRange(startMonth, endMonth);
    return `${count} (${range})`;
  };

  // Dynamic period configuration
  const periodConfigs: PeriodConfig[] = useMemo(() => [
    { startDate: null, endDate: "15/01", editableCount: 12, lockedCount: 0 },
    { startDate: "16/01", endDate: "15/04", editableCount: 9, lockedCount: 3 },
    { startDate: "16/04", endDate: "15/07", editableCount: 6, lockedCount: 6 },
    { startDate: "16/07", endDate: "15/10", editableCount: 3, lockedCount: 9 },
    { startDate: "16/10", endDate: "15/01", editableCount: 0, lockedCount: 12 },
  ], []);

  // Generate period data dynamically
  const periodData = useMemo(() => {
    return periodConfigs.map((config) => {
      const { startDate, endDate, editableCount, lockedCount } = config;
      
      // Calculate editable range
      const editableStartMonth = lockedCount + 1;
      const editableEndMonth = 12;
      
      // Calculate locked range
      const lockedStartMonth = 1;
      const lockedEndMonth = lockedCount;
      
      return {
        period: startDate ? `${startDate} - ${endDate}` : `Until ${endDate}`,
        editableMonths: formatMonthInfo(editableCount, editableStartMonth, editableEndMonth),
        lockedMonths: formatMonthInfo(lockedCount, lockedStartMonth, lockedEndMonth),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodConfigs]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Forecast Editing & Creating Time Table</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
          Editing availability changes throughout the year. Months gradually
          reduce as time progress.
        </Typography>
        <TableContainer
          className="PageScantablewrap"
          sx={{ marginTop: "16px" }}
        >
          <Table aria-label="collapsible table" size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell>Editable Months</TableCell>
                <TableCell>Locked Months</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {periodData.map((row, index) => (
                <TableRow 
                  key={index}
                  sx={{ 
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                  }}
                >
                  <TableCell>{row.period}</TableCell>
                  <TableCell>{row.editableMonths}</TableCell>
                  <TableCell>{row.lockedMonths}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined" color="secondary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PeriodMoreInfoModal;